package nl.volleyplanner.web

import kotlinx.browser.window
import kotlinx.datetime.LocalDateTime
import kotlinx.datetime.format
import nl.volleyplanner.extensions.now

enum class LogLevel() {
    Trace,
    Debug,
    Info,
    Warn,
    Error,
    Disabled
}

object LogConfig {
    val loggers = mutableMapOf<String, LogLevel>()
    val defaultLevel = window.localStorage.getItem("defaultLogLevel")
        ?.let { defaultLogLevel -> LogLevel.entries.first { it.name == defaultLogLevel } } ?: LogLevel.Warn

    fun addLogger(prefix: String, logLevel: LogLevel) = loggers.put(prefix, logLevel)
}

class Logger private constructor(val name: String, var logEnabledFromLevel: LogLevel) {
    companion object {
        val DEV_TOOLS_LOGGER = Logger("VolleyplannerDevTools", LogLevel.Info)
        val ERROR_LOGGER = Logger("ErrorLogger", LogLevel.Error)
        val loggerMap = mutableMapOf<String, Logger>()

        fun getLogger(name: String) : Logger = loggerMap.getOrPut(name) {
            if (loggerMap.isEmpty()) setupLoggers()

            val logConfig = LogConfig.loggers
                .filterKeys { name.startsWith(it) }
                .maxByOrNull { it.key.length }

            Logger(name, logConfig?.value ?: LogConfig.defaultLevel)
        }

        fun updateLoggers(prefix: String, level: LogLevel) {
            loggerMap
                .filterKeys { it.startsWith(prefix) }
                .forEach { (name, logger) ->
                    DEV_TOOLS_LOGGER.info("Updating logger $name to level $level")
                    logger.logEnabledFromLevel = level
                }
        }
    }

    fun logMessage(level: LogLevel, vararg args: Any, message: () -> String) {
        if (logEnabledFromLevel <= level) {
            val dateTimeStr = LocalDateTime.now().format(DATE_TIME_ISO_FORMAT)
            val message = "$dateTimeStr ${level}\t${name}: ${message()}"

            when (level) {
                LogLevel.Trace -> console.log(message, *args)
                LogLevel.Debug -> console.log(message, *args)
                LogLevel.Info -> console.info(message, *args)
                LogLevel.Warn -> console.warn(message, *args)
                LogLevel.Error -> console.error(message, *args)
                else -> return
            }
        }
    }

    fun trace(message: String) = logMessage(LogLevel.Trace) { message }

    fun trace(message: () -> String) = logMessage(LogLevel.Trace, message = message)

    fun debug(message: String) = logMessage(LogLevel.Debug) { message }

    fun debug(message: () -> String) = logMessage(LogLevel.Debug, message = message)

    fun info(message: String) = logMessage(LogLevel.Info) { message }

    fun info(message: () -> String) = logMessage(LogLevel.Info, message = message)

    fun warn(message: String) = logMessage(LogLevel.Warn) { message }

    fun warn(message: () -> String) = logMessage(LogLevel.Warn, message = message)

    fun error(message: () -> String) = logMessage(LogLevel.Error, message = message)

    fun throwable(message: String, throwable: Throwable) {
        val dateTimeStr = LocalDateTime.now().format(DATE_TIME_ISO_FORMAT)
        val logMessage = "$dateTimeStr EXCEPTION\t$message - ${throwable::class.simpleName}: ${throwable.message}"
        console.error(logMessage)
        console.error(throwable)

        val dynamicThrowable = throwable.asDynamic()
        val stack = throwable.asDynamic().stack
        if (stack != null) {
            console.error("JS stack:", stack)
        }

        throwable.cause?.let { cause ->
            throwable("Caused by", cause)
        }
    }
}